import React, { useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  LayoutDashboard,
  Building,
  GitBranch,
  Users,
  UserCog,
  Handshake,
  Settings,
  Ruler,
  Hash,
  Percent,
  FolderTree,
  Calculator,
  ListTree,
  Package,
  ShoppingCart,
  Tags,
  Receipt,
  FileText,
  FileClock,
  DollarSign,
  ShoppingBag,
  Undo2,
  WifiOff,
  Banknote,
  Shield,
  MapPin,
  TrendingUp,
  CheckSquare,
  MessageSquareMore,
  ListTodo,
  Briefcase,
  ClipboardCheck,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

// Define navigation items structure
interface NavItem {
  to: string
  icon: React.ElementType
  label: string
}

interface SidebarProps {
  isCollapsed: boolean
  onToggle: () => void
  onNavClick?: () => void
  isMobile?: boolean
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle, onNavClick, isMobile }) => {
  const location = useLocation()

  // Check if a nav item is active, considering both path and query params
  const isNavItemActive = useCallback((to: string): boolean => {
    const [itemPath, itemSearch] = to.split('?')
    if (location.pathname !== itemPath) return false
    if (!itemSearch) {
      // For items without search params, active only if current URL also has no 'group' param
      const currentParams = new URLSearchParams(location.search)
      return !currentParams.has('group') && !currentParams.has('task_type')
    }
    // For items with search params, verify all params match
    const itemParams = new URLSearchParams(itemSearch)
    const currentParams = new URLSearchParams(location.search)
    for (const [key, value] of itemParams.entries()) {
      if (currentParams.get(key) !== value) return false
    }
    return true
  }, [location])

  // Navigation items (top level only)
  const navItems: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/party?group=Client', icon: Users, label: 'Clients' },
    { to: '/party?group=Employee', icon: UserCog, label: 'Employees' },
    { to: '/party?group=Consultants', icon: Handshake, label: 'Consultants' },
    { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
    { to: '/tasks?task_type=todo', icon: ListTodo, label: 'To-Do' },
    { to: '/tasks?task_type=enquiry', icon: MessageSquareMore, label: 'Enquiries' }
  ]

  // ERP navigation items (moved under ERP group)
  const erpNavItems: NavItem[] = [
    { to: '/organizations', icon: Building, label: 'Organizations' },
    { to: '/sites', icon: GitBranch, label: 'Sites' },
    { to: '/pricing', icon: Tags, label: 'Pricing' }
  ]

  // Items navigation items
  const itemsNavItems: NavItem[] = [
    { to: '/items', icon: Package, label: 'Items' },
    { to: '/items/collections', icon: FolderTree, label: 'Item Collections' }
  ]

  // Quotes navigation items
  const quotesNavItems: NavItem[] = [
    { to: '/quotations/list', icon: FileClock, label: 'Quotations' },
    { to: '/sales-orders/list', icon: ShoppingCart, label: 'Sales Orders' }
  ]

  // Sales navigation items
  const salesNavItems: NavItem[] = [
    { to: '/sales-invoices/list', icon: FileText, label: 'Invoices' },
    { to: '/payments-in/list', icon: Receipt, label: 'Payments In' },
    { to: '/cheques/list', icon: Banknote, label: 'Cheque Register' },
    { to: '/credit-notes/list', icon: Undo2, label: 'Credit Notes' },
    { to: '/offline-invoices/list', icon: WifiOff, label: 'Offline Invoices' }
  ]

  // Transactions navigation items
  const transactionsNavItems: NavItem[] = [
    { to: '/voucher-types', icon: FileText, label: 'Voucher Types' },
    { to: '/vouchers', icon: Receipt, label: 'Vouchers' }
    // Add other transaction-related items here
  ]

  // Settings navigation items
  const settingsNavItems: NavItem[] = [
    { to: '/settings/organization-settings', icon: Building, label: 'Organization Settings' },
    { to: '/settings/units', icon: Ruler, label: 'Units' },
    { to: '/settings/hsn-codes', icon: Hash, label: 'HSN Codes' },
    { to: '/settings/tax-rates', icon: Percent, label: 'Tax Rates' },
    { to: '/settings/categories', icon: FolderTree, label: 'Categories' },
    { to: '/settings/compliance-services', icon: ClipboardCheck, label: 'Compliance Services' },
    { to: '/settings/return-generation', icon: Calendar, label: 'Return Generation' },
    { to: '/settings/teams', icon: Users, label: 'Teams' },
    { to: '/pricing/bulk-update', icon: Tags, label: 'Bulk Price Update' },
    { to: '/pricing/site-availability', icon: MapPin, label: 'Site Availability' }
  ]

  // Accounting navigation items
  const accountingNavItems: NavItem[] = [
    { to: '/accounting/chart-of-accounts', icon: ListTree, label: 'Chart of Accounts' },
    { to: '/accounting/account-statement', icon: FileText, label: 'Account Statement' },
    { to: '/day-end-closure', icon: Banknote, label: 'Day-End Cash Closure' }
  ]


  // Reporting navigation items
  const reportingNavItems: NavItem[] = [
    { to: '/admin/reporting/sales', icon: TrendingUp, label: 'Sales Report' },
    { to: '/admin/reporting/parties', icon: Users, label: 'Clients Report' },
    { to: '/admin/reporting/customers', icon: Users, label: 'Customer Metrics' }
  ]

  // Admin navigation items (contains sub-accordions)
  const adminSubGroups = [
    {
      title: 'Transactions',
      icon: Receipt,
      items: transactionsNavItems
    },
    {
      title: 'Accounting',
      icon: Calculator,
      items: accountingNavItems
    },
    {
      title: 'Reporting',
      icon: FileText,
      items: reportingNavItems
    },
    {
      title: 'Settings',
      icon: Settings,
      items: settingsNavItems
    }
  ]

  // Use the onToggle prop for sidebar toggle
  const toggleSidebar = (): void => onToggle()

  // Handle navigation click
  const handleNavClick = (e: React.MouseEvent, item: NavItem) => {
    // Check if Ctrl/Cmd is pressed to open in new tab
    if ((e.ctrlKey || e.metaKey) && window.api?.tabs) {
      e.preventDefault()
      window.api.tabs.create(item.to, item.label)
    }
    // Otherwise, let normal navigation happen in current tab
    
    // Call onNavClick for mobile to close sidebar
    if (onNavClick) {
      onNavClick()
    }
  }

  // Render NavLink component
  const renderNavLink = (item: NavItem): JSX.Element => {
    const active = isNavItemActive(item.to)
    const navLink = (
      <Link
        key={item.to}
        to={item.to}
        onClick={(e) => handleNavClick(e, item)}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
          active && 'bg-primary/10 text-primary',
          isCollapsed && 'justify-center'
        )}
      >
        <item.icon className="h-4 w-4" />
        {!isCollapsed && <span>{item.label}</span>}
      </Link>
    )

    if (isCollapsed) {
      return (
        <Tooltip key={item.to}>
          <TooltipTrigger asChild>{navLink}</TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            <p>{item.label}</p>
            <p className="text-xs text-muted-foreground">Ctrl/Cmd+Click for new tab</p>
          </TooltipContent>
        </Tooltip>
      )
    }

    return navLink
  }

  // Render a collapsed menu with popover
  const renderCollapsedMenuWithPopover = (
    icon: React.ElementType,
    label: string,
    items: NavItem[]
  ): JSX.Element => {
    const Icon = icon
    return (
      <Tooltip>
        <Popover>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-lg text-muted-foreground hover:text-primary"
              >
                <Icon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <PopoverContent side="right" align="start" className="w-56 p-1">
            <div className="flex flex-col gap-1">
              <span className="px-2 py-1.5 text-sm font-semibold">{label}</span>
              {items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={(e) => handleNavClick(e, item)}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
                    isNavItemActive(item.to) && 'bg-primary/10 text-primary'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <TooltipContent side="right" className="font-medium">
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn(
        'flex h-full flex-col p-2 bg-sidebar text-sidebar-foreground border-r',
        isCollapsed && !isMobile && 'items-center',
        isMobile && 'w-64'
      )}>
        {/* App Logo/Name */}
        <div className="mb-4 flex items-center justify-between p-2">
          {(!isCollapsed || isMobile) && <span className="text-lg font-semibold tracking-tight">ETax</span>}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className={cn(
                  "rounded-lg",
                  (!isCollapsed || isMobile) ? "ml-auto" : "",
                  isMobile && "lg:hidden" // Hide toggle on mobile as we have X to close
                )}
              >
                {isMobile ? (
                  <X className="h-4 w-4" />
                ) : isCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side={isCollapsed ? 'right' : 'left'}>
              <p>{isMobile ? 'Close menu' : (isCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Navigation Links */}
        <ScrollArea className="flex-1">
          <nav className="flex flex-col gap-1 px-2">
            {/* Main navigation items */}
            {navItems.map(renderNavLink)}

            {/* ERP Accordion */}
            {isCollapsed && !isMobile ? (
              renderCollapsedMenuWithPopover(Briefcase, 'ERP', [
                ...erpNavItems,
                ...itemsNavItems,
                ...quotesNavItems,
                ...salesNavItems,
                { to: '/purchase-invoices/list', icon: Receipt, label: 'Purchase Invoices' },
                { to: '/debit-notes/list', icon: Undo2, label: 'Debit Notes' },
                { to: '/expenses/list', icon: DollarSign, label: 'Expenses' },
                { to: '/payments-out/list', icon: Receipt, label: 'Payments Out' },
                ...transactionsNavItems,
                ...accountingNavItems,
                ...reportingNavItems,
                ...settingsNavItems
              ])
            ) : (
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="erp" className="border-none">
                  <AccordionTrigger className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <Briefcase className="h-4 w-4" />
                    <span className="flex-1 text-left">ERP</span>
                  </AccordionTrigger>
                  <AccordionContent className="pl-7 pb-1">
                    {/* Direct ERP items (Tasks, Pricing) */}
                    <div className="flex flex-col gap-1 mb-2">
                      {erpNavItems.map(renderNavLink)}
                    </div>

                    {/* Nested accordions within ERP */}
                    <Accordion type="single" collapsible className="w-full">
                      {/* Items Nested Accordion */}
                      <AccordionItem value="items" className="border-none">
                        <AccordionTrigger className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          <Package className="h-4 w-4" />
                          <span className="flex-1 text-left">Items</span>
                        </AccordionTrigger>
                        <AccordionContent className="pl-7 pb-1 flex flex-col gap-1">
                          {itemsNavItems.map(renderNavLink)}
                        </AccordionContent>
                      </AccordionItem>

                      {/* Quotes Nested Accordion */}
                      <AccordionItem value="quotes" className="border-none">
                        <AccordionTrigger className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          <FileClock className="h-4 w-4" />
                          <span className="flex-1 text-left">Quotes</span>
                        </AccordionTrigger>
                        <AccordionContent className="pl-7 pb-1 flex flex-col gap-1">
                          {quotesNavItems.map(renderNavLink)}
                        </AccordionContent>
                      </AccordionItem>

                      {/* Sales Nested Accordion */}
                      <AccordionItem value="sales" className="border-none">
                        <AccordionTrigger className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          <ShoppingCart className="h-4 w-4" />
                          <span className="flex-1 text-left">Sales</span>
                        </AccordionTrigger>
                        <AccordionContent className="pl-7 pb-1 flex flex-col gap-1">
                          {salesNavItems.map(renderNavLink)}
                        </AccordionContent>
                      </AccordionItem>

                      {/* Purchases & Expenses Nested Accordion */}
                      <AccordionItem value="purchases-expenses" className="border-none">
                        <AccordionTrigger className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          <ShoppingBag className="h-4 w-4" />
                          <span className="flex-1 text-left">Purchases & Expenses</span>
                        </AccordionTrigger>
                        <AccordionContent className="pl-7 pb-1 flex flex-col gap-1">
                          {renderNavLink({
                            to: '/purchase-invoices/list',
                            icon: Receipt,
                            label: 'Purchase Invoices'
                          })}
                          {renderNavLink({ to: '/debit-notes/list', icon: Undo2, label: 'Debit Notes' })}
                          {renderNavLink({ to: '/expenses/list', icon: DollarSign, label: 'Expenses' })}
                          {renderNavLink({
                            to: '/payments-out/list',
                            icon: Receipt,
                            label: 'Payments Out'
                          })}
                        </AccordionContent>
                      </AccordionItem>

                      {/* Admin Nested Accordion with Sub-groups */}
                      <AccordionItem value="admin" className="border-none">
                        <AccordionTrigger className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          <Shield className="h-4 w-4" />
                          <span className="flex-1 text-left">Admin</span>
                        </AccordionTrigger>
                        <AccordionContent className="pl-7 pb-1">
                          {/* Nested accordions for admin sub-groups */}
                          <Accordion type="single" collapsible className="w-full">
                            {adminSubGroups.map((group) => (
                              <AccordionItem key={group.title} value={group.title} className="border-none">
                                <AccordionTrigger className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground hover:no-underline [&[data-state=open]>svg]:rotate-180">
                                  <group.icon className="h-4 w-4" />
                                  <span className="flex-1 text-left">{group.title}</span>
                                </AccordionTrigger>
                                <AccordionContent className="pl-7 pb-1 flex flex-col gap-1">
                                  {group.items.map(renderNavLink)}
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

          </nav>
        </ScrollArea>
      </div>
    </TooltipProvider>
  )
}

export default Sidebar
